import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Copy } from 'lucide-react';
import { Product, OptimizedContent } from '../../types/product';
import { PublishToShopifyButton } from '../PublishToShopifyButton';
import { formatStoreName } from '../../utils/formatters';
import { useToast } from '../ui/use-toast';

interface ProductPreviewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  optimizedContent: OptimizedContent | undefined;
  marketplace: 'Shopify' | 'Google Shopping' | 'Amazon';
  lastOptimized: string;
}

interface PreviewSectionProps {
  title: string;
  content: string;
  onCopy: () => void;
  isBulletPoints?: boolean;
}

const PreviewSection: React.FC<PreviewSectionProps> = ({ title, content, onCopy, isBulletPoints }) => {
  const [showCopied, setShowCopied] = useState(false);
  const toast = useToast();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      toast.toast({
        description: "Copied to clipboard",
        duration: 2000,
      });
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.toast({
        description: "Failed to copy to clipboard",
        variant: "destructive",
        duration: 2000,
      });
    }
  };

  // Format content with proper paragraphs and spacing
  const formatContent = (text: string) => {
    if (!text) return '';
    
    if (isBulletPoints) {
      return text.split('\n').map((point: string, index: number) => (
        <li key={index} className="mb-3 leading-relaxed">{point.trim()}</li>
      ));
    }

    // Split text into paragraphs and preserve formatting
    return text.split('\n\n').map((paragraph: string, index: number) => (
      <p key={index} className="mb-4 leading-relaxed">
        {paragraph.split('\n').map((line, lineIdx) => (
          <React.Fragment key={lineIdx}>
            {line}
            {lineIdx < paragraph.split('\n').length - 1 && <br />}
          </React.Fragment>
        ))}
      </p>
    ));
  };

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCopy}
            className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 flex items-center gap-1 relative"
          >
            <Copy className="h-4 w-4" />
            <span className="text-sm">Copy</span>
            {showCopied && (
              <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded animate-fade-in-out">
                Copied!
              </span>
            )}
          </button>
        </div>
      </div>
      <div className={`text-gray-800 p-4 bg-gray-50 rounded-lg ${
        isBulletPoints ? '' : 'whitespace-pre-wrap'
      }`}>
        {isBulletPoints ? (
          <ul className="list-disc pl-4 space-y-2">
            {formatContent(content)}
          </ul>
        ) : (
          formatContent(content)
        )}
      </div>
    </div>
  );
};

const formatDate = (dateString: string): string => {
  if (!dateString || dateString === 'Not yet optimized') {
    return 'Not yet optimized';
  }
  
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      });
    }
  } catch (error) {
    console.error('Error formatting date:', error);
  }
  
  return 'Not yet optimized';
};

const ProductPreviewDialog: React.FC<ProductPreviewDialogProps> = ({
  isOpen,
  onClose,
  product,
  optimizedContent,
  marketplace,
  lastOptimized,
}) => {
  const [loadedImages, setLoadedImages] = useState<{ [key: string]: boolean }>({});
  const [shouldLoadImages, setShouldLoadImages] = useState(false);
  const toast = useToast();

  // Reset image states when dialog opens/closes or product changes
  useEffect(() => {
    setLoadedImages({});
    if (isOpen) {
      // Small delay to ensure dialog is mounted and visible
      const timer = setTimeout(() => {
        setShouldLoadImages(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setShouldLoadImages(false);
    }
  }, [isOpen, product.id]);

  const handleImageLoad = (imageUrl: string) => {
    setLoadedImages(prev => ({ ...prev, [imageUrl]: true }));
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.toast({
        description: "Copied to clipboard",
        duration: 2000,
      });
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.toast({
        description: "Failed to copy to clipboard",
        variant: "destructive",
        duration: 2000,
      });
    }
  };

  const getMarketplaceStyle = () => {
    switch (marketplace) {
      case 'Shopify':
        return 'bg-green-50 text-green-800 border-green-200';
      case 'Google Shopping':
        return 'bg-blue-50 text-blue-800 border-blue-200';
      case 'Amazon':
        return 'bg-yellow-50 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-50 text-gray-800 border-gray-200';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="space-y-4">
          <div className="flex items-center justify-between pr-8">
            <DialogTitle>
              {marketplace} Preview
            </DialogTitle>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 text-sm rounded-full border ${getMarketplaceStyle()}`}>
                {marketplace}
              </span>
              {marketplace === 'Shopify' && optimizedContent?.title && optimizedContent?.description && (
                <PublishToShopifyButton
                  storeName={formatStoreName(product.storeName)}
                  productId={product.id.replace('SHOPIFY-', '')}
                  documentId={product.id}
                  title={optimizedContent.title}
                  description={optimizedContent.description}
                  bulletPoints={optimizedContent.bulletPoints || []}
                  pageTitle={optimizedContent.pageTitle}
                  metaDescription={optimizedContent.metaDescription}
                  onPublishComplete={onClose}
                />
              )}
            </div>
          </div>
          <p className="text-sm text-gray-500">
            Last optimized: {formatDate(lastOptimized)}
          </p>
        </DialogHeader>

        <div className="space-y-6">
          {/* Product Header - Image and Title side by side */}
          <div className="flex gap-6 items-start">
            {/* Product Image */}
            {shouldLoadImages && product.images[0] && (
              <div className="relative w-48 h-48">
                <img
                  src={product.images[0].url}
                  alt={optimizedContent?.title || product.basicInfo.title}
                  className={`w-full h-full object-contain rounded-lg transition-opacity duration-300 ${
                    loadedImages[product.images[0].url] ? 'opacity-100' : 'opacity-0'
                  }`}
                  onLoad={() => handleImageLoad(product.images[0].url)}
                />
                {!loadedImages[product.images[0].url] && (
                  <div className="absolute inset-0 bg-gray-100 animate-pulse rounded-lg" />
                )}
              </div>
            )}
            {/* Title Section */}
            <div className="flex-1">
              <PreviewSection
                title="Title"
                content={optimizedContent?.title || ''}
                onCopy={() => copyToClipboard(optimizedContent?.title || '')}

              />
            </div>
          </div>

          {/* Rest of Product Information */}
          <div className="space-y-6">
            <PreviewSection
              title="Description"
              content={optimizedContent?.description || ''}
              onCopy={() => copyToClipboard(optimizedContent?.description || '')}

            />

            {optimizedContent?.bulletPoints && (
              <PreviewSection
                title="Bullet Points"
                content={optimizedContent.bulletPoints.join('\n')}
                onCopy={() => copyToClipboard(optimizedContent.bulletPoints.join('\n'))}

                isBulletPoints={true}
              />
            )}

            {/* Meta Description and Page Title for Shopify */}
            {marketplace === 'Shopify' && (
              <>
                {optimizedContent?.pageTitle && (
                  <PreviewSection
                    title="Page Title"
                    content={optimizedContent.pageTitle}
                    onCopy={() => copyToClipboard(optimizedContent.pageTitle || '')}

                  />
                )}
                {optimizedContent?.metaDescription && (
                  <PreviewSection
                    title="Meta Description"
                    content={optimizedContent.metaDescription}
                    onCopy={() => copyToClipboard(optimizedContent.metaDescription || '')}

                  />
                )}
              </>
            )}

            {/* Product Category and Type for Google Shopping */}
            {marketplace === 'Google Shopping' && (
              <>
                {optimizedContent?.productCategory && (
                  <PreviewSection
                    title="Product Category"
                    content={optimizedContent.productCategory || ''}
                    onCopy={() => optimizedContent.productCategory && copyToClipboard(optimizedContent.productCategory)}

                  />
                )}
                {optimizedContent?.productType && (
                  <PreviewSection
                    title="Product Type"
                    content={optimizedContent.productType || ''}
                    onCopy={() => optimizedContent.productType && copyToClipboard(optimizedContent.productType)}

                  />
                )}
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductPreviewDialog;